import richTextInitialHtml from "./rich_text_initial_html"

const THEMES = ["brand", "muted", "success", "warning", "lime", "error"]

function normalizeInitialItems(raw) {
  const rows = Array.isArray(raw) ? raw : []
  if (rows.length === 0) return [{ content: "", items: [] }]

  return rows.map((row) => {
    if (row && typeof row === "object" && "content" in row) {
      const content = typeof row.content === "string" ? row.content : ""
      return {
        content: richTextInitialHtml(content),
        items: []
      }
    }
    return {
      content: richTextInitialHtml(row == null ? "" : String(row)),
      items: []
    }
  })
}

export default class CardListTool {
  static get enableLineBreaks() {
    return true
  }

  static get toolbox() {
    return {
      title: "List card",
      icon: "<span class=\"icon-[lucide--list] size-4\"></span>"
    }
  }

  static get sanitize() {
    return {
      title: false,
      theme: false,
      items: {
        content: {
          br: true,
          div: true
        }
      }
    }
  }

  constructor({ data, readOnly } = {}) {
    this.readOnly = Boolean(readOnly)
    this.data = {
      theme: this.normalizeTheme(data?.theme),
      title: data?.title || "",
      items: normalizeInitialItems(data?.items)
    }
    this.themeButtons = new Map()
    this.wrapper = null
    this.titleInput = null
    this.listEl = null
    this.addButton = null
  }

  render() {
    this.wrapper = document.createElement("div")
    this.wrapper.className = "editorjs-list-card"

    this.titleInput = document.createElement("input")
    this.titleInput.type = "text"
    this.titleInput.placeholder = "Header"
    this.titleInput.className = "editorjs-list-card__title"
    this.titleInput.value = this.data.title

    this.listEl = document.createElement("ul")
    this.listEl.className = "editorjs-list-card__list"

    this.data.items.forEach((row) => {
      this.listEl.appendChild(this.createListItem(row.content || ""))
    })

    this.addButton = document.createElement("button")
    this.addButton.type = "button"
    this.addButton.className = "editorjs-list-card__add btn btn-ghost btn-xs mt-2"
    this.addButton.textContent = "Add list item"
    if (this.readOnly) {
      this.addButton.classList.add("hidden")
    }
    this.addButton.addEventListener("click", () => {
      this.listEl.appendChild(this.createListItem(""))
    })

    this.titleInput.addEventListener("input", () => {
      this.data.title = this.titleInput.value
    })

    this.wrapper.append(this.titleInput, this.listEl, this.addButton)
    this.applyThemeClass()
    return this.wrapper
  }

  createListItem(initialHtml) {
    const li = document.createElement("li")
    li.className = "editorjs-list-card__item"

    const row = document.createElement("div")
    row.className = "editorjs-list-card__item-row"

    const editor = document.createElement("div")
    editor.className = "editorjs-list-card__item-text"
    editor.contentEditable = this.readOnly ? "false" : "true"
    editor.innerHTML = initialHtml || ""

    row.appendChild(editor)

    if (!this.readOnly) {
      const removeBtn = document.createElement("button")
      removeBtn.type = "button"
      removeBtn.className = "editorjs-list-card__remove btn btn-ghost btn-xs btn-square shrink-0"
      removeBtn.setAttribute("aria-label", "Remove list item")
      removeBtn.textContent = "×"
      removeBtn.addEventListener("click", () => {
        if (this.listEl.querySelectorAll(":scope > li").length <= 1) return
        li.remove()
      })
      row.appendChild(removeBtn)
    }

    li.appendChild(row)
    return li
  }

  renderSettings() {
    const settings = document.createElement("div")
    settings.className = "editorjs-list-card-settings"

    THEMES.forEach((theme) => {
      const button = document.createElement("button")
      button.type = "button"
      button.className = "editorjs-list-card-settings__btn"
      button.textContent = this.themeLabel(theme)
      this.themeButtons.set(theme, button)

      button.addEventListener("click", () => {
        this.data.theme = theme
        this.updateSettingsButtonsState()
        this.applyThemeClass()
      })

      settings.appendChild(button)
    })

    this.updateSettingsButtonsState()
    return settings
  }

  save() {
    const items = []
    this.listEl.querySelectorAll(":scope > li .editorjs-list-card__item-text").forEach((editor) => {
      items.push({
        content: editor.innerHTML || "",
        items: []
      })
    })

    return {
      title: this.titleInput?.value || "",
      theme: this.normalizeTheme(this.data.theme),
      items
    }
  }

  validate(savedData) {
    const title = (savedData?.title || "").trim()
    if (title) return true

    const items = Array.isArray(savedData?.items) ? savedData.items : []
    for (const item of items) {
      const html = item?.content || ""
      const tmp = document.createElement("div")
      tmp.innerHTML = html
      if ((tmp.textContent || "").trim()) return true
    }
    return false
  }

  applyThemeClass() {
    if (!this.wrapper) return

    THEMES.forEach((theme) => {
      this.wrapper.classList.remove(`editorjs-list-card--${theme}`)
    })

    this.wrapper.classList.add(`editorjs-list-card--${this.normalizeTheme(this.data.theme)}`)
  }

  updateSettingsButtonsState() {
    this.themeButtons.forEach((button, theme) => {
      button.classList.toggle("editorjs-list-card-settings__btn--active", this.data.theme === theme)
    })
  }

  normalizeTheme(theme) {
    return THEMES.includes(theme) ? theme : "brand"
  }

  themeLabel(theme) {
    switch (theme) {
    case "brand":
      return "Brand"
    case "muted":
      return "Muted"
    case "success":
      return "Success"
    case "warning":
      return "Warning"
    case "lime":
      return "Lime"
    case "error":
      return "Error"
    default:
      return "Brand"
    }
  }
}
